import {Component, OnInit, ɵProviderRecord, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {environment} from '../../environments/environment';
import {AccountService} from '../core/auth/account.service';
import {ILogin, Login} from '../../shared/model/login.model';
import {UnifiedSigninResponse, UnifiedSigninStatus} from "../../shared/model/unified-signin-response";
import {firstValueFrom} from 'rxjs';
import {MatCard, MatCardTitle} from '@angular/material/card';
import {MatFormField, MatLabel, MatError} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';


@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.css'],
  imports: [MatCard, MatCardTitle, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatIcon, MatButton, MatProgressSpinner, RouterLink]
})
export class SigninComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly accountService = inject(AccountService);


  email = '';
  form: UntypedFormGroup = this.fb.group({
    email: [this.email, [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    mfa: ['', []]
  });
  error = '';
  isLoading = false;
  apiUrl = environment.apiUrl;
  showPassword = false;
  login: ILogin = new Login();
  displayMfa = false;

  ngOnInit(): void {
    if (this.accountService.isAuthenticated()) {
      this.router.navigate(['bills']).then();
    }
  }

  showError(error: string): void {
    this.toastr.error(error, 'Erreur', {timeOut: 3000});
  }

  async connect(): Promise<void> {
    this.isLoading = true;

    try {
      const response = await firstValueFrom(this.http.post<UnifiedSigninResponse>(
        `${this.apiUrl}/auth/signin`,
        this.login
      ));

      this.handleSigninResponse(response);
    } catch (error: unknown) {
      this.isLoading = false;
      this.onReceiveError(error);
    }
  }

  private handleSigninResponse(response: UnifiedSigninResponse): void {
    switch (response.status) {
      case UnifiedSigninStatus.OK:
        if (response.token) {
          this.redirectAfterLogin(response.token);
        } else {
          this.isLoading = false;
          this.onReceiveError('Token manquant dans la réponse');
        }
        break;

      case UnifiedSigninStatus.MFA_REQUIRED:
        this.displayMfa = true;
        this.isLoading = false;
        break;

      case UnifiedSigninStatus.ERROR:
      case UnifiedSigninStatus.ERR_USER_NOT_FOUND:
      case UnifiedSigninStatus.ERR_INACTIVE_ACC:
      case UnifiedSigninStatus.ERR_TOO_MANY_CRED:
        this.isLoading = false;
        this.onReceiveError(response.code || response.status);
        break;

      default:
        this.isLoading = false;
        this.onReceiveError('Statut de réponse inconnu');
        break;
    }
  }

  redirectAfterLogin(token: string): void {
    SigninComponent.setSessionToken(token);
    localStorage.setItem('email', this.login.username);
    const jsonParseProvider = (JSON.parse(atob(token.split('.')[1])) as ɵProviderRecord).provider;
    if (typeof jsonParseProvider === "string") {
      localStorage.setItem('provider', jsonParseProvider);
    }
    if (localStorage.getItem('ieo_cb_reference') != null && localStorage.getItem('ieo_cb_amount') != null) {
      const body = {
        email: this.email,
        reference: localStorage.getItem('ieo_cb_reference'),
        amount: localStorage.getItem('ieo_cb_amount')
      };
      this.http.post(this.apiUrl + '/checkout/send-recap-mail', body, {observe: 'response', responseType: 'text'})
        .subscribe({
          next: () => {
            localStorage.removeItem('ieo_cb_reference');
            localStorage.removeItem('ieo_cb_amount');
          },
          error: () => {
            localStorage.removeItem('ieo_cb_reference');
            localStorage.removeItem('ieo_cb_amount');
          }
        });
    }
    this.router.navigate(['switch-company']).then();
  }

  onReceiveError(error: unknown): void {
    let errorMessage: string;

    if (error == null) {
      errorMessage = 'Erreur dans la paire email/mot de passe';
    } else if (typeof error === 'string') {
      const isUnauthorized = error.search('Unauthorized');
      if (isUnauthorized !== -1) {
        errorMessage = 'Erreur dans la paire email/mot de passe';
      } else {
        switch (error) {
          case 'ERR_USER_NOT_FOUND':
          case 'ERR_INACTIVE_ACC':
            errorMessage = 'Erreur dans la paire email/mot de passe';
            break;

          case 'ERR_TOO_MANY_CRED':
            errorMessage = 'Trop de tentatives invalides. ' +
              'Merci d\'utiliser la fonction \'Réinitialiser mon mot de passe\'';
            break;

          default:
            errorMessage = 'Merci de réessayer dans quelques instants';
            break;
        }
      }
    } else {
      errorMessage = 'une erreur est survenue';
    }

    this.error = errorMessage;
    this.showError(this.error);
  }

  private static setSessionToken(jwt: string): void {
    localStorage.setItem('accessToken', jwt);
  }

  clickShowPassword(): void {
    this.showPassword = !this.showPassword;
  }
}
